import { toSafeString } from './toSafeString.js';

const KNOWLEDGE_SKILL_PATH_PREFIX = 'skills';
const KNOWLEDGE_SKILL_PATH_PREFIXES = Object.freeze([KNOWLEDGE_SKILL_PATH_PREFIX, 'knowledge/skills']);

const KNOWLEDGE_SKILL_LABELS = Object.freeze({
  knowledge_list: 'Checking your skills',
  knowledge_read: 'Reading a skill',
  knowledge_write: 'Saving a skill',
  knowledge_stat: 'Checking a skill',
  knowledge_move: 'Moving a skill',
  knowledge_delete: 'Deleting a skill',
  knowledge_search_query: 'Searching your skills',
  knowledge_share: 'Sharing a skill'
});

const toNormalizedPath = (value) => {
  const normalized = toSafeString(value).replace(/\\/g, '/').replace(/^\/+/, '').trim();

  if (!normalized) {
    return null;
  }

  return normalized;
};

const isKnowledgeSkillPath = (value) => {
  const normalizedPath = toNormalizedPath(value);

  if (!normalizedPath) {
    return false;
  }

  return KNOWLEDGE_SKILL_PATH_PREFIXES.some((prefix) => {
    return normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`);
  });
};

const collectCandidatePaths = (value, accumulator) => {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      collectCandidatePaths(item, accumulator);
    }

    return;
  }

  if (typeof value === 'object') {
    const safeObject = value;

    accumulator.push(safeObject?.path, safeObject?.pathPrefix, safeObject?.filePath, safeObject?.sourcePath, safeObject?.targetPath, safeObject?.fromPath, safeObject?.toPath);

    if (Array.isArray(safeObject?.files)) {
      for (const file of safeObject.files) {
        accumulator.push(file?.path);
      }
    }
  }
};

const hasKnowledgeSkillTarget = (args = {}) => {
  const safeArgs = args && typeof args === 'object' ? args : {};
  const candidatePaths = [];

  collectCandidatePaths(safeArgs, candidatePaths);
  collectCandidatePaths(safeArgs?.parsedArgs, candidatePaths);
  collectCandidatePaths(safeArgs?.request, candidatePaths);
  collectCandidatePaths(safeArgs?.request?.parsedArgs, candidatePaths);
  collectCandidatePaths(safeArgs?.response, candidatePaths);
  collectCandidatePaths(safeArgs?.response?.data, candidatePaths);
  collectCandidatePaths(safeArgs?.response?.output, candidatePaths);
  collectCandidatePaths(safeArgs?.response?.output?.data, candidatePaths);
  collectCandidatePaths(safeArgs?.result, candidatePaths);
  collectCandidatePaths(safeArgs?.result?.data, candidatePaths);
  collectCandidatePaths(safeArgs?.output, candidatePaths);
  collectCandidatePaths(safeArgs?.output?.data, candidatePaths);
  collectCandidatePaths(safeArgs?.data, candidatePaths);

  return candidatePaths.some((path) => isKnowledgeSkillPath(path));
};

const isPieveSkillToolCall = ({ toolName, args } = {}) => {
  const normalizedToolName = toSafeString(toolName).toLowerCase();

  if (!normalizedToolName) {
    return false;
  }

  if (normalizedToolName.startsWith('skills_')) {
    return true;
  }

  if (!Object.hasOwn(KNOWLEDGE_SKILL_LABELS, normalizedToolName)) {
    return false;
  }

  return hasKnowledgeSkillTarget(args);
};

const resolvePieveKnowledgeToolCallDisplayText = ({ toolName, args } = {}) => {
  const normalizedToolName = toSafeString(toolName).toLowerCase();

  if (!Object.hasOwn(KNOWLEDGE_SKILL_LABELS, normalizedToolName)) {
    return null;
  }

  if (!hasKnowledgeSkillTarget(args)) {
    return null;
  }

  return KNOWLEDGE_SKILL_LABELS[normalizedToolName];
};

export default resolvePieveKnowledgeToolCallDisplayText;
export { KNOWLEDGE_SKILL_LABELS, KNOWLEDGE_SKILL_PATH_PREFIX, KNOWLEDGE_SKILL_PATH_PREFIXES, collectCandidatePaths, hasKnowledgeSkillTarget, isPieveSkillToolCall, isKnowledgeSkillPath };
